import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Capacitor } from '@capacitor/core';
import { ShareService } from 'src/app/services/share.service';

export type ShareChannel = 'whatsapp' | 'instagram' | 'email' | 'copy';

/**
 * Modal de compartir genérico.
 *
 * Recibe **strings ya resueltos** (`message`, `link`, `emailSubject`) en vez de
 * modelos de dominio: es lo que le permite servir a cualquier flujo. Los modales
 * de share existentes exigen un `Ticket`/`RecentTicket` completo, y por eso no
 * se pueden reusar donde la respuesta del backend trae otra forma de datos.
 */
@Component({
  selector: 'app-share-modal',
  templateUrl: './share-modal.component.html',
  styleUrls: ['./share-modal.component.scss'],
  standalone: false,
})
export class ShareModalComponent implements OnInit {
  @Input() title?: string;
  @Input() message = '';
  /** Opcional: si viene, se muestra como bloque aparte y clickeable. */
  @Input() link?: string;
  @Input() emailSubject?: string;
  @Input() channels: ShareChannel[] = ['whatsapp', 'instagram', 'copy'];
  @Input() dismissOnShare = false;

  isNative = false;

  constructor(
    private readonly modalCtrl: ModalController,
    private readonly shareService: ShareService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.isNative = Capacitor.isNativePlatform();
  }

  get modalTitle(): string {
    return this.title ?? 'share.title';
  }

  get fullShareMessage(): string {
    return this.link ? `${this.message}\n\n${this.link}` : this.message;
  }

  hasChannel(channel: ShareChannel): boolean {
    return this.channels.includes(channel);
  }

  onWhatsApp(): void {
    this.shareService.shareWhatsApp(this.fullShareMessage);
    this.dismissIfNeeded();
  }

  async onInstagram(): Promise<void> {
    await this.shareService.shareInstagram(this.fullShareMessage);
    this.dismissIfNeeded();
  }

  onEmail(): void {
    // El fallback se traduce acá: `ShareService.shareEmail` mete el asunto
    // crudo en el mailto, así que pasarle la clave dejaría "share.email-subject"
    // como asunto del mail.
    const subject = this.emailSubject ?? this.translate.instant('share.email-subject');

    this.shareService.shareEmail(this.fullShareMessage, subject);
    this.dismissIfNeeded();
  }

  async onCopy(): Promise<void> {
    await this.shareService.copyText(this.fullShareMessage);
    this.dismissIfNeeded();
  }

  async onNative(): Promise<void> {
    await this.shareService.shareNative(this.fullShareMessage);
    this.dismissIfNeeded();
  }

  closeModal(): void {
    this.modalCtrl.dismiss();
  }

  private dismissIfNeeded(): void {
    if (this.dismissOnShare) {
      this.closeModal();
    }
  }
}
